import {
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SourceIndicator } from '@/components/treatment';
import { useMatter } from '@/hooks/useMatter';
import { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';
import { usePostgridContactsListQuery } from '@/services/api/postgridService';
import { Source } from '@/services/types/treatment-types';

interface RecordsChannelCardProps {
  channel: ReadDepartmentRecordsChannel;
}

// Helper to convert vendor enum/slug to user-friendly label
const getVendorDisplayText = (vendor: string | null | undefined) => {
  if (!vendor) return '';
  const slug = vendor.toLowerCase();
  switch (slug) {
    case 'carfax_crashdocs':
    case 'carfax_crashdocs'.toLowerCase():
      return 'CrashDocs';
    case 'lexis_nexis_buycrash':
    case 'lexis_nexis_buycrash'.toLowerCase():
      return 'BuyCrash';
    default:
      return vendor.replace('_', ' ');
  }
};

// Helper to get action data for a records channel
const getChannelAction = (
  channel: ReadDepartmentRecordsChannel,
  hasContact: boolean,
) => {
  const { handle, major_vendor: majorVendor } = channel;

  // Priority 1: Known vendor portals
  if (majorVendor && majorVendor !== 'not_a_portal') {
    return {
      icon: <GlobeAltIcon className="h-4 w-4 text-blue-600" />,
      label: `Check ${getVendorDisplayText(majorVendor)}`,
      action: 'external',
      url: handle.value || '',
    };
  }

  // Priority 2: Handle type based actions
  const handleValue = handle.value || '';

  switch (handle.handle_type?.toUpperCase()) {
    case 'WEBSITE':
    case 'URL': {
      const urlStem = handleValue
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/.*$/, '');
      return {
        icon: <GlobeAltIcon className="h-4 w-4 text-blue-600" />,
        label: `Visit ${urlStem}`,
        action: 'external',
        url: handleValue,
      };
    }

    case 'PHONE':
      return {
        icon: <PhoneIcon className="h-4 w-4 text-green-600" />,
        label: `Call ${handleValue}`,
        action: 'phone',
        url: `tel:${handleValue}`,
      };

    case 'EMAIL':
      return {
        icon: <EnvelopeIcon className="h-4 w-4 text-gray-600" />,
        label: `Email ${handleValue.split('@')[0]}`,
        action: 'email',
        url: `mailto:${handleValue}`,
      };

    default:
      // Fallback to mail if we have a contact
      if (hasContact) {
        return {
          icon: <CreditCardIcon className="h-4 w-4 text-purple-600" />,
          label: 'Request by Mail',
          action: 'mail',
        };
      }

      return {
        icon: <EnvelopeIcon className="h-4 w-4 text-gray-500" />,
        label: handleValue,
        action: 'none',
      };
  }
};

export const RecordsChannelCard = ({ channel }: RecordsChannelCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { matterId } = useMatter();
  const navigate = useNavigate();

  const { id } = channel;
  const { handle } = channel;
  const docUrl = channel.required_document_file_url;
  const { instructions } = channel;
  const feeAmount = channel.fee_amount;

  // Fetch PostGrid contact associated with this police channel (max 1)
  const { data: contactsResponse } = usePostgridContactsListQuery(
    { policeChannelId: id, isActive: true, limit: 10 },
    { skip: !id },
  );

  const contacts = contactsResponse?.results || [];
  const contact = contacts.length > 0 ? contacts[0] : null;
  const contactData = contact as {
    id: string;
    company_name?: string;
    first_name?: string;
    last_name?: string;
  } | null;

  const action = getChannelAction(channel, !!contact);
  const hasSecondaryInfo = feeAmount || docUrl || instructions;

  const handleActionClick = () => {
    if (action.action === 'mail' && matterId && contactData) {
      // Navigate to cheque wizard with prefilled recipient
      const params = new URLSearchParams();
      params.set('to', contactData.id);

      // Add fee amount if available
      if (feeAmount) {
        params.set('amount', feeAmount.toString());
      }

      // Add memo with department and channel info
      const memoText = `Police records request - ${contactData.company_name || contactData.first_name || 'Police Department'}`;
      params.set('memo', memoText);

      const queryString = params.toString();
      const url = `/matters/${matterId}/mail/cheque-wizard${queryString ? `?${queryString}` : ''}`;

      navigate(url);
    } else if (action.url) {
      window.open(action.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="group border border-gray-200 rounded bg-white hover:border-gray-300 transition-colors">
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            {action.icon}
            <span className="text-sm text-gray-700 truncate">
              {action.label}
            </span>
            {handle?.sources && (handle.sources as Source[]).length > 0 && (
              <SourceIndicator sources={handle.sources as Source[]} />
            )}
          </div>

          <div className="flex items-center space-x-2">
            {action.action !== 'none' && (
              <button
                type="button"
                onClick={handleActionClick}
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                {action.action === 'mail' && 'Send Mail'}
                {action.action === 'external' && channel.major_vendor && 'Open'}
                {action.action === 'external' &&
                  !channel.major_vendor &&
                  'Visit'}
                {action.action === 'phone' && 'Call'}
                {action.action === 'email' && 'Email'}
              </button>
            )}

            {hasSecondaryInfo && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg
                  className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {isExpanded && hasSecondaryInfo && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            <div className="text-xs text-gray-500">{handle.value}</div>

            {(feeAmount || docUrl) && (
              <div className="flex items-center space-x-3 text-xs">
                {feeAmount && (
                  <span className="flex items-center space-x-1 text-gray-600">
                    <CurrencyDollarIcon className="h-3 w-3" />${feeAmount}
                  </span>
                )}
                {docUrl && (
                  <a
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                  >
                    <DocumentArrowDownIcon className="h-3 w-3" />
                    Form
                  </a>
                )}
              </div>
            )}

            {instructions && (
              <div className="text-xs text-gray-600 leading-relaxed bg-gray-50 rounded p-2">
                {instructions}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
